import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import type { User } from "@supabase/supabase-js";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { queryClient } from "../hooks/useDataCache";
import { clearLastKnownHousehold } from "../hooks/useHousehold";
import { ALL_HOUSEHOLDS_QUERY_KEY } from "../hooks/useHouseholds";
import { clearLastKnownLicense } from "../hooks/useLicense";
import {
  clearLastKnownHouseholdId,
  USER_HOUSEHOLD_ID_KEY,
} from "../hooks/useUser";
import {
  consumeExplicitSignOut,
  markExplicitSignOut,
} from "../lib/authSignals";
import { realtimeWorker } from "../lib/realtimeWorker";
import { supabase } from "../lib/supabase";
import { router } from "../router";
import {
  acceptInvitation,
  getInvitationByToken,
  type HouseholdInvitation,
} from "../services/invitationService";
import { licenseService } from "../services/licenseService";
import LandingPage from "./LandingPage";

interface AuthGuardProps {
  children: React.ReactNode;
}

// Module-level guard: prevents the invitation mismatch sign-out from running
// more than once across StrictMode double-mounts or any other re-render.
let invitationMismatchHandled = false;

// Module-level store for a pending invitation on email mismatch.
// React state is wiped on StrictMode unmount/remount, so we park the
// invitation here and seed component state from it on every mount.
let _pendingInvitationStore: HouseholdInvitation | null = null;

// Auth guard that checks auth state, sets realtime worker auth, and renders children when authenticated
// Shows a loading UI while checking authentication and falls back to AuthForm when unauthenticated
// Handles password reset flow when on /reset-password route
export default function AuthGuard({ children }: AuthGuardProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  // When a logged-in user clicks an invitation meant for a different email,
  // we sign them out and hold the invitation here so LandingPage/AuthForm
  // can pre-fill the sign-in form without relying on localStorage or URL params.
  // Seeded from the module-level store so it survives StrictMode remounts.
  const [pendingInvitation, setPendingInvitation] =
    useState<HouseholdInvitation | null>(_pendingInvitationStore);
  const userRef = useRef<User | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      // Check if there's an invitation token in the URL
      const params = new URLSearchParams(window.location.search);
      const invitationToken = params.get("invitation");

      // Set a maximum wait time for the auth session check to prevent
      // infinite loading on slow/hanging networks.
      const timeoutId = setTimeout(() => {
        setLoading(false);
      }, 5000);

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        clearTimeout(timeoutId);

        // If there's an invitation token and a user is already logged in
        if (invitationToken && session?.user) {
          // Guard against double-execution (React StrictMode, concurrent renders)
          if (invitationMismatchHandled) {
            // StrictMode re-mount: still need to resolve loading state.
            userRef.current = session.user;
            setUser(session.user);
            realtimeWorker.setAuth(session.access_token);
            setLoading(false);
            return;
          }
          invitationMismatchHandled = true;

          // Check if the logged-in user matches the invitation
          const invitation = await getInvitationByToken(invitationToken);

          // Compare emails case-insensitively — invitation email is stored
          // lowercase but the session email may differ in casing.
          const emailMatches =
            invitation &&
            invitation.email.toLowerCase() ===
              session.user.email?.toLowerCase();

          if (emailMatches) {
            // User matches invitation — accept it and stay on the dashboard.
            // Clear the token from the URL regardless of outcome so they
            // don't get stuck in a redirect loop on refresh.
            window.history.replaceState({}, "", "/");

            const success = await acceptInvitation(
              invitationToken,
              session.user.id,
            );

            if (success) {
              toast.success(
                `You've joined the ${invitation.household_name} household!`,
              );
              // Switch the active household to the newly joined one.
              localStorage.setItem(
                "pantry_active_household_id",
                String(invitation.household_id),
              );
              // Invalidate household caches so the switcher picks up the
              // new membership without requiring a page reload.
              queryClient.invalidateQueries({
                queryKey: ALL_HOUSEHOLDS_QUERY_KEY,
              });
              queryClient.invalidateQueries({
                queryKey: USER_HOUSEHOLD_ID_KEY,
              });
            } else {
              // Accept failed (already a member, expired, etc.) — still
              // proceed to dashboard rather than signing the user out.
              console.warn(
                "🟡 acceptInvitation returned false for logged-in user — proceeding to dashboard anyway.",
              );
            }

            // Always proceed to dashboard when the email matched.
            userRef.current = session.user;
            setUser(session.user);
            realtimeWorker.setAuth(session.access_token);
            setLoading(false);
            return;
          }

          // Email mismatch — hold the invitation in React state so AuthForm
          // can pre-fill the sign-in form, then sign out the current user.
          // Do NOT strip the token from the URL here — after sign-out the router
          // navigates to "/" and AuthGuard remounts. The logged-out path will
          // then see the token, look up the invitation, and pass it to LandingPage.
          if (invitation) {
            _pendingInvitationStore = invitation;
            setPendingInvitation(invitation);
          }
          markExplicitSignOut();
          await supabase.auth.signOut();
          userRef.current = null;
          setUser(null);
          setLoading(false);
          return;
        }

        // Logged-out user arriving via an invitation link — look up the
        // invitation so LandingPage/AuthForm can pre-fill the sign-in/join form.
        // Do NOT strip the token from the URL here: StrictMode mounts the effect
        // twice, and removing the token on the first run means the second run
        // sees no token and renders a plain landing page. AuthForm will strip
        // the token from the URL once it actually handles it.
        if (invitationToken && !session?.user) {
          const invitation = await getInvitationByToken(invitationToken);
          if (invitation && invitation.status === "pending") {
            _pendingInvitationStore = invitation;
            setPendingInvitation(invitation);
          }
        }

        userRef.current = session?.user || null;
        setUser(session?.user || null);
        realtimeWorker.setAuth(session?.access_token ?? "");
        setLoading(false);
      } catch (error) {
        console.error("Auth check failed:", error);
        setLoading(false);
      } finally {
        clearTimeout(timeoutId);
      }
    };

    checkAuth();

    // On native, email confirmation links are HTTPS URLs that Android App Links
    // intercepts and hands to the app. Supabase's auth server processes the token
    // and redirects to the site URL with the session in the hash fragment
    // (#access_token=...&type=signup). The Capacitor WebView never applies that
    // hash to window.location, so onAuthStateChange won't fire automatically.
    // We listen for the deep-link URL and call setSession() manually, which
    // triggers onAuthStateChange with SIGNED_IN and completes the login.
    let deepLinkListenerPromise: ReturnType<typeof App.addListener> | null =
      null;
    if (Capacitor.isNativePlatform()) {
      const handleAuthDeepLink = async (url: string) => {
        try {
          const hashIndex = url.indexOf("#");
          if (hashIndex === -1) return;
          const params = new URLSearchParams(url.slice(hashIndex + 1));
          const accessToken = params.get("access_token");
          const refreshToken = params.get("refresh_token");
          const type = params.get("type");
          // Only handle signup/magiclink — recovery is also handled here
          // so AuthGuard (always mounted) catches it and navigates to /reset-password
          if (accessToken && refreshToken && type === "recovery") {
            await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
            void router.navigate({ to: "/reset-password", replace: true });
          } else if (
            accessToken &&
            refreshToken &&
            (type === "signup" || type === "magiclink")
          ) {
            await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
          }
        } catch (err) {
          console.error("🔴 AuthGuard: error handling auth deep link:", err);
        }
      };

      deepLinkListenerPromise = App.addListener("appUrlOpen", ({ url }) => {
        void handleAuthDeepLink(url);
      });

      // Also check the launch URL for cold-starts triggered by the link.
      void App.getLaunchUrl().then((launch) => {
        if (launch?.url) void handleAuthDeepLink(launch.url);
      });

      // Fall through to the subscription setup below — do NOT return early.
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (_event === "SIGNED_OUT") {
        // Reset the invitation guard so the next invitation link works.
        invitationMismatchHandled = false;
        // Clear any pending invitation so a normal logout doesn't show
        // the sign-in-and-join screen to the next user.
        _pendingInvitationStore = null;
        setPendingInvitation(null);
        const wasExplicit = consumeExplicitSignOut();
        // Always clear cached data on sign-out so stale queries don't survive
        // into the next session.
        queryClient.clear();
        clearLastKnownHouseholdId();
        clearLastKnownHousehold();
        clearLastKnownLicense();
        // Clear the active household override so the next login starts fresh
        // rather than jumping to a household from a previous session.
        localStorage.removeItem("pantry_active_household_id");
        if (!wasExplicit && userRef.current !== null) {
          // The session was terminated automatically (e.g. the refresh token
          // expired or was invalidated by Supabase). Let the user know.
          toast.error("Your session has expired. Please sign in again.");
        }
        void router.navigate({ to: "/", replace: true });
      }

      // Capture whether there was already a logged-in user BEFORE this event
      // so we can distinguish a genuine new login from a silent token refresh.
      const previousUser = userRef.current;

      userRef.current = session?.user || null;
      setUser(session?.user || null);
      realtimeWorker.setAuth(session?.access_token ?? "");

      // After email confirmation the user gets a SIGNED_IN event. Check whether
      // there is a pending household invitation that was stored in localStorage
      // before the confirmation email was sent (invitation + email confirmation flow).
      if (_event === "SIGNED_IN" && session?.user) {
        if (previousUser === null) {
          void router.navigate({ to: "/inventory", replace: true });
          // Invalidate (not remove) so existing cached data stays visible
          // while the fresh fetch runs — prevents "No Household Found" flash.
          queryClient.invalidateQueries({ queryKey: USER_HOUSEHOLD_ID_KEY });
        }

        void (async () => {
          const pendingStr = localStorage.getItem("pantry_pending_invitation");
          if (!pendingStr) return;

          try {
            const pending = JSON.parse(pendingStr) as {
              token: string;
              householdName: string | null;
              householdId: string | null;
              email: string | null;
            };

            // Safety check: only accept the invitation if the signed-in user's
            // email matches the one that initiated the sign-up, so a different
            // user logging in later doesn't accidentally consume a stale entry.
            if (pending.email && session.user?.email !== pending.email) {
              localStorage.removeItem("pantry_pending_invitation");
              return;
            }

            localStorage.removeItem("pantry_pending_invitation");
            const success = await acceptInvitation(
              pending.token,
              session.user?.id ?? "",
            );
            if (success) {
              toast.success(
                pending.householdName
                  ? `Welcome to the ${pending.householdName} household!`
                  : "Welcome to your household!",
              );
              // Switch the active household to the newly joined one.
              if (pending.householdId) {
                localStorage.setItem(
                  "pantry_active_household_id",
                  String(pending.householdId),
                );
              }
              // Invalidate household caches so the switcher picks up the
              // new membership without requiring a page reload.
              queryClient.invalidateQueries({
                queryKey: ALL_HOUSEHOLDS_QUERY_KEY,
              });
              queryClient.invalidateQueries({
                queryKey: USER_HOUSEHOLD_ID_KEY,
              });
            }
          } catch {
            localStorage.removeItem("pantry_pending_invitation");
          }
        })();

        // On native Android/iOS, activate the app license on every sign-in.
        // The edge function is idempotent — it no-ops if already active.
        // This ensures the license is granted even if the user signs up
        // before activation has ever run (e.g. first-time install).
        const nativePlatform = Capacitor.isNativePlatform()
          ? Capacitor.getPlatform()
          : null;
        if (nativePlatform === "android" || nativePlatform === "ios") {
          void (async () => {
            try {
              await licenseService.activateAppLicense(
                nativePlatform === "ios" ? "app_store" : "google_play",
              );
              queryClient.invalidateQueries({ queryKey: ["license"] });
            } catch (err) {
              console.error(
                "🔴 Failed to activate app license on sign-in:",
                err,
              );
            }
          })();
        }
      }
    });

    return () => {
      subscription.unsubscribe();
      if (deepLinkListenerPromise) {
        void deepLinkListenerPromise.then((l) => l.remove());
      }
    };
  }, []);

  // Show loading spinner while auth is resolving
  if (loading) {
    return (
      <div className="bg-gradient-animated flex min-h-dvh items-center justify-center">
        <output className="text-center" aria-live="polite">
          <div
            className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600 dark:border-blue-700 dark:border-t-blue-400"
            aria-hidden="true"
          ></div>
        </output>
      </div>
    );
  }

  if (!user) {
    return <LandingPage pendingInvitation={pendingInvitation} />;
  }

  return <>{children}</>;
}
